import {
  Button,
  Grid,
  Input,
  Typography,
  Radio,
  RadioGroup,
  FormControl,
  FormControlLabel,
  Box,
} from '@mui/material';
import React, { useId, useState } from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as Camera } from '../../assets/icons/camera.svg';
import { ChooseOne } from 'src/components/AssignmentAssistant/ChooseOne';
import plans from 'src/configuration/writing-plans';
import { DocumentFormat } from 'src/types/DocumentSettings';
import { DOCUMENT_STARTERS } from 'src/configuration/starters';

function WritingSupportRadioOption({
  value,
  checked,
  onChange,
  title,
  description,
  sx,
}: {
  value: string;
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
  title: React.ReactNode;
  description: React.ReactNode;
  sx?: any;
}) {
  return (
    <FormControlLabel
      value={value}
      control={<Radio checked={checked} onChange={(e) => onChange(e, value)} />}
      label={
        <Box>
          <Typography
            fontWeight={700}
            variant="subtitle1"
            color="primary.main"
            sx={{ marginTop: '9px',  lineHeight: '140%' }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ lineHeight: '120%' }}
          >
            {description}
          </Typography>
        </Box>
      }
      sx={{ alignItems: 'flex-start', ...sx }}
    />
  );
}

// Language options with codes as values
const LANGUAGE_OPTIONS = [
  { value: 'en-US', label: 'English (American)' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'vi-VN', label: 'Vietnamese' },
  { value: 'ru-RU', label: 'Russian' },
  { value: 'uk-UA', label: 'Ukrainian' },
  { value: 'zh-hans-CN', label: 'Chinese (Simplified, Mandarin)' },
  { value: 'browser-language', label: 'Detect from student\'s browser' },
];

export default function AlignmentInput() {
  const [topic, setTopic] = useState('');
  const [objectUrl, setObjectUrl] = useState('');
  const [gradeLevel, setGradeLevel] = useState('4th');
  const [writingPlanId, setWritingPlanId] = useState<string>('auto');
  const [writingSupportOption, setWritingSupportOption] = useState<
    'organizer_sentence_starters' | 'organizer_only' | 'outline' | 'essay'
  >('organizer_sentence_starters');
  const [supportLanguage, setSupportLanguage] = useState('en-US');
  const inputId = useId();
  const navigate = useNavigate();

  // Filter down to only writing plans that have at least one
  // starter with the plan.
  const selectablePlans = plans.filter((plan) => {
    return DOCUMENT_STARTERS.some(
      (starter) => starter.writingPlanId === plan.id
      && starter.format === DocumentFormat.DOC
    );
  });

  const [{ isOver, isDragging }, drop] = useDrop<
    {
      files: File[];
      dataTransfer: DataTransfer;
    },
    void,
    {
      isOver: boolean;
      canDrop: boolean;
      isDragging: boolean;
    }
  >(
    () => ({
      accept: [NativeTypes.FILE],
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
        isDragging: monitor.getItem() !== null,
      }),
      drop(item: { files: File[] }) {
        const file = item.files?.[0];
        if (file) {
          setObjectUrl(URL.createObjectURL(file));
        }
      },
    }),
    [],
  );

  return (
    <Grid container gap={4}>
      <Grid
        item
        container
        sx={[
          {
            alignItems: 'end',
            padding: '16px 20px 16px 20px',
            border: (theme) => `1px solid ${theme.palette.primary.main}`,
            backgroundColor: '#F6F6FA',
          },
        ]}
        ref={drop}
      >
        {objectUrl && (
          <img
            src={objectUrl}
            alt="object"
            style={{
              width: 'auto',
              alignSelf: 'top',
              height: '2.8rem',
              objectFit: 'contain',
              marginRight: '20px',
              marginBottom: '5px',
              borderRadius: '5px',
            }}
          />
        )}

        <Grid item sx={{ flex: 1 }}>
          <Input
            type="text"
            multiline
            fullWidth
            placeholder={
              isDragging
                ? 'Drop image here'
                : 'Enter a topic, text, URL, or image'
            }
            value={topic}
            onKeyDown={(event) => {
              event.stopPropagation();
            }}
            onKeyUp={(event) => {
              event.stopPropagation();
            }}
            onChange={(event) => {
              setTopic(event.target.value);
            }}
            autoFocus
            sx={[
              {
                fontSize: '14px',
                color: 'black',
                fontWeight: 'bold',
                fontFamily: 'Inter',
                borderBottom: 'none',
                borderRadius: '5px',
                padding: 0,
                lineHeight: '26px',

                'textarea::placeholder': {
                  color: 'rgba(0, 0, 0, 0.6)',
                },
              },

              isOver && {
                borderColor: 'gray',
              },
            ]}
          />
        </Grid>
        <Grid item>
          <label htmlFor={inputId}>
            <Button
              variant="text"
              component="span"
              startIcon={<Camera />}
              style={{
                textTransform: 'none',
                lineHeight: '12px',
                padding: '0px',
              }}
            >
              Upload Image
            </Button>
            <input
              id={inputId}
              type="file"
              accept="image/*"
              style={{
                display: 'none',
              }}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  setObjectUrl(URL.createObjectURL(file));
                }
              }}
            />
          </label>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Left: Enhancements */}
        <Grid item direction="column" xs={12} md={5} spacing={4}>
          <Grid item>
            <Typography
              fontWeight={700}
              fontSize="1.1rem"
              color="primary"
              className="form-section-name"
              sx={{
                marginBottom: '20px',
              }}
            >
              Settings
            </Typography>
          </Grid>
          <Grid item container direction="row" spacing={2} rowGap={2}>
            <Grid item xs={12} md={6}>
              <ChooseOne
                label="Grade Level"
                isRequired
                options={[
                  '2nd',
                  '3rd',
                  '4th',
                  '5th',
                  '6th',
                  '7th',
                  '8th',
                  '9th',
                  '10th',
                  '11th',
                  '12th',
                ]}
                onChange={setGradeLevel}
                value={gradeLevel || undefined}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ChooseOne
                label="Writing Plan"
                isRequired={true}
                options={[
                  {
                    value: 'auto',
                    label: 'Auto',
                  },
                  ...selectablePlans.map((plan) => ({
                    value: plan.id,
                    label: plan.title,
                  })),
                ]}
                onChange={setWritingPlanId}
                value={writingPlanId ?? ''}
              />
            </Grid>
            <Grid item xs={12}>
              <ChooseOne
                label="Support Language"
                helpText="The language in which students will receive support from the Writing Buddy and other scaffolds"
                isRequired={true}
                options={LANGUAGE_OPTIONS}
                onChange={setSupportLanguage}
                value={supportLanguage}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Right: Writing Supports */}
        <Grid item xs={12} md={7}>
          <Typography
            fontWeight={700}
            fontSize="1.1rem"
            color="primary"
            className="form-section-name"
          >
            Writing Supports
          </Typography>
          <div
            style={{
              color: '#444',
              fontSize: 14,
              marginBottom: 12,
              marginLeft: 1,
              lineHeight: '130%',
            }}
          >
            Writing supports help students organize their thinking and guide
            their writing. Choose the right level of structure for your activity.
          </div>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              name="writing-supports"
              value={writingSupportOption}
              onChange={(e) => setWritingSupportOption(e.target.value as any)}
              sx={{
                gap: '6px',
              }}
            >
              <WritingSupportRadioOption
                value="organizer_sentence_starters"
                checked={writingSupportOption === 'organizer_sentence_starters'}
                onChange={(_, value) => setWritingSupportOption(value as any)}
                title={<span>Maximum Support: <span style={{ fontWeight: 400 }}>Graphic Organizer + Sentence Starters</span></span>}
                description="Provide an organizer with helpful sentence starters to guide student writing."
              />
              <WritingSupportRadioOption
                value="organizer_only"
                checked={writingSupportOption === 'organizer_only'}
                onChange={(_, value) => setWritingSupportOption(value as any)}
                title={<span>Moderate Support: <span style={{ fontWeight: 400 }}>Graphic Organizer only</span></span>}
                description="Provide a blank writing organizer to help students plan."
              />
              <WritingSupportRadioOption
                value="outline"
                checked={writingSupportOption === 'outline'}
                onChange={(_, value) => setWritingSupportOption(value as any)}
                title={<span>Minimum Support: <span style={{ fontWeight: 400 }}>Students build their own outline</span></span>}
                description="Encourage students to structure their writing independently."
              />
            </RadioGroup>
          </FormControl>
        </Grid>
      </Grid>

      <Grid container justifyContent="flex-end" sx={{ mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          style={{
            height: '48px',
            width: '160px',
            textTransform: 'none',
          }}
          onClick={() => {
            let includeBlocks = true;
            let includeSentenceStarters = true;
            if (writingSupportOption === 'organizer_only') {
              includeBlocks = true;
              includeSentenceStarters = false;
            } else if (
              writingSupportOption === 'outline' ||
              writingSupportOption === 'essay'
            ) {
              includeBlocks = false;
              includeSentenceStarters = false;
            }

            navigate(
              `/document/align?${new URLSearchParams({
                topic,
                objectDataUrl: objectUrl,
                gradeLevel,
                supportLanguage,
                ...(writingPlanId && writingPlanId !== 'auto' ? { writingPlanId } : {}),
                includeBlocks: includeBlocks ? 'true' : 'false',
                includeSentenceStarters: includeSentenceStarters
                  ? 'true'
                  : 'false',
              }).toString()}`,
              { replace: true },
            );
          }}
        >
          Next
        </Button>
      </Grid>
    </Grid>
  );
}
